import type { IMonitoringTaskRepository } from "../repositories/MonitoringTaskRepository";
import type {
  VisitTask,
  CreateMonitoringTaskRequest,
  UpdateMonitoringTaskRequest,
  UpdateTaskStatusRequest,
  MonitoringTaskListResponse,
  MonitoringTaskFilters,
  TaskStatistics,
  TaskStatus,
} from "../pages/MonitoringTracker/types/monitoringVisit.types";
import { getErrorMessage, isApiError } from "../utils/errorMessage";
export interface IMonitoringTaskUseCase {
  createTask(task: CreateMonitoringTaskRequest): Promise<VisitTask>;
  getTasks(
    page?: number,
    limit?: number,
    filters?: MonitoringTaskFilters,
  ): Promise<MonitoringTaskListResponse>;
  getTaskById(id: string): Promise<VisitTask>;
  updateTask(
    id: string,
    updates: UpdateMonitoringTaskRequest,
  ): Promise<VisitTask>;
  deleteTask(id: string): Promise<void>;
  updateTaskStatus(
    id: string,
    status: UpdateTaskStatusRequest,
  ): Promise<VisitTask>;
  getTasksByVisit(visitId: string): Promise<VisitTask[]>;
  getTaskStatisticsByVisit(visitId: string): Promise<TaskStatistics>;
  getOverdueTasks(): Promise<VisitTask[]>;
}
export class MonitoringTaskUseCase implements IMonitoringTaskUseCase {
  constructor(private monitoringTaskRepository: IMonitoringTaskRepository) {}
  async createTask(task: CreateMonitoringTaskRequest): Promise<VisitTask> {
    // Validate required fields
    if (!task.visitId?.trim()) {
      throw new Error("Visit ID is required");
    }
    if (!task.taskName?.trim()) {
      throw new Error("Task name is required");
    }
    if (task.taskName.length > 255) {
      throw new Error("Task name must be 255 characters or less");
    }
    if (!task.dueDate?.trim()) {
      throw new Error("Due date is required");
    }
    // Validate date format
    const dueDate = new Date(task.dueDate);
    if (isNaN(dueDate.getTime())) {
      throw new Error("Invalid due date format");
    }
    try {
      return await this.monitoringTaskRepository.createTask(task);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to create monitoring task",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid monitoring task data provided"),
        );
      }
      throw new Error(
        getErrorMessage(error, "Failed to create monitoring task"),
      );
    }
  }
  async getTasks(
    page = 1,
    limit = 10,
    filters?: MonitoringTaskFilters,
  ): Promise<MonitoringTaskListResponse> {
    // Validate pagination parameters
    if (page < 1) {
      throw new Error("Page number must be greater than 0");
    }
    if (limit < 1 || limit > 100) {
      throw new Error("Limit must be between 1 and 100");
    }
    return await this.monitoringTaskRepository.getTasks(page, limit, filters);
  }
  async getTaskById(id: string): Promise<VisitTask> {
    if (!id?.trim()) {
      throw new Error("Monitoring task ID is required");
    }
    return await this.monitoringTaskRepository.getTaskById(id);
  }
  async updateTask(
    id: string,
    updates: UpdateMonitoringTaskRequest,
  ): Promise<VisitTask> {
    if (!id?.trim()) {
      throw new Error("Monitoring task ID is required");
    }
    // Validate task name length if provided
    if (updates.taskName && updates.taskName.length > 255) {
      throw new Error("Task name must be 255 characters or less");
    }
    // Validate date format if provided
    if (updates.dueDate) {
      const dueDate = new Date(updates.dueDate);
      if (isNaN(dueDate.getTime())) {
        throw new Error("Invalid due date format");
      }
    }
    // Validate status if provided
    if (updates.status) {
      const validStatuses: TaskStatus[] = [
        "Pending",
        "In Progress",
        "Completed",
      ];
      if (!validStatuses.includes(updates.status)) {
        throw new Error("Invalid task status");
      }
    }
    return await this.monitoringTaskRepository.updateTask(id, updates);
  }
  async deleteTask(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Monitoring task ID is required");
    }
    await this.monitoringTaskRepository.deleteTask(id);
  }
  async updateTaskStatus(
    id: string,
    statusUpdate: UpdateTaskStatusRequest,
  ): Promise<VisitTask> {
    if (!id?.trim()) {
      throw new Error("Monitoring task ID is required");
    }
    if (!statusUpdate.status) {
      throw new Error("Task status is required");
    }
    // Validate status
    const validStatuses: TaskStatus[] = ["Pending", "In Progress", "Completed"];
    if (!validStatuses.includes(statusUpdate.status)) {
      throw new Error("Invalid task status");
    }
    return await this.monitoringTaskRepository.updateTaskStatus(
      id,
      statusUpdate,
    );
  }
  async getTasksByVisit(visitId: string): Promise<VisitTask[]> {
    if (!visitId?.trim()) {
      throw new Error("Visit ID is required");
    }
    return await this.monitoringTaskRepository.getTasksByVisit(visitId);
  }
  async getTaskStatisticsByVisit(visitId: string): Promise<TaskStatistics> {
    if (!visitId?.trim()) {
      throw new Error("Visit ID is required");
    }
    return await this.monitoringTaskRepository.getTaskStatisticsByVisit(
      visitId,
    );
  }
  async getOverdueTasks(): Promise<VisitTask[]> {
    return await this.monitoringTaskRepository.getOverdueTasks();
  }
}
